import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserAgreements, updateUserAgreement } from "@modules/userAgreement";
import {
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    Stack,
} from "@mzc-pdc/ui";
import Typography from "@components_v2/typography/typography";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";
import { IconClose } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import StickyHeaderDialog from "@components_v2/dialog/sticky-header-dialog";
import { useTranslation } from "react-i18next";
import compareVersions, { VERSION_CM_14011_USER_AGREEMENT } from "@cores/version";
import { useLocation } from "react-router-dom";
import { setIsAgreementRequired } from "@modules/global";

const UserAgreement = ({ children, ...props }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { pathname } = useLocation();

    const global = useSelector(({ global }) => global);
    const stage = useSelector(({ stage }) => stage);
    const userAgreement = useSelector(({ userAgreement }) => userAgreement.detail);

    const [openTermsAgreement, setOpenTermsAgreement] = useState(false);
    const [hasAgreed, setHasAgreed] = useState(false);
    const [updatePending, setUpdatePending] = useState(false);

    useEffect(() => {
        if (
            compareVersions(stage.version, VERSION_CM_14011_USER_AGREEMENT) >= 0 &&
            pathname !== "/" &&
            global.isAgreementRequired
        ) {
            const params = { status: "ACTIVE" };
            try {
                dispatch(getUserAgreements({ params }));
            } catch (e) {
                console.error(e);
            }
        }
    }, [global.isAgreementRequired]);

    const disagreedPart = useMemo(() => {
        return userAgreement?.parts.find((part) => !part.hasAgreed && part.isRequired);
    }, [userAgreement]);

    const onClickCheckAgreement = useCallback(async () => {
        try {
            setUpdatePending(true);
            const params = {
                type: "UPDATE_AGREEMENT",
                data: {
                    parts: [
                        {
                            id: disagreedPart?.id,
                            hasAgreed,
                        },
                    ],
                },
            };
            await dispatch(updateUserAgreement({ id: userAgreement.id, params }));
            const userAgreementParams = { status: "ACTIVE" };
            const response = await dispatch(getUserAgreements({ params: userAgreementParams }));

            const _disagreedPart = response?.detail?.parts.find((part) => !part.hasAgreed && part.isRequired);
            if (_disagreedPart) setHasAgreed(false);
            else {
                setOpenTermsAgreement(false);
                dispatch(setIsAgreementRequired({ isAgreementRequired: false }));
            }
        } catch (e) {
            console.error(e);
        } finally {
            setUpdatePending(false);
        }
    }, [userAgreement, disagreedPart, hasAgreed]);

    const onClose = () => {
        setOpenTermsAgreement(false);
    };

    useEffect(() => {
        if (
            userAgreement &&
            disagreedPart &&
            compareVersions(stage.version, VERSION_CM_14011_USER_AGREEMENT) >= 0 &&
            pathname !== "/"
        ) {
            setOpenTermsAgreement(true);
        }
    }, [stage, userAgreement, pathname]);

    return (
        <>
            <StickyHeaderDialog
                open={openTermsAgreement}
                onClose={onClose}
                variant="expandable"
                maxWidth="768px"
                sx={{ ".MuiDialogContent-root": { padding: 0, scrollbarGutter: "unset" } }}
            >
                <DialogTitle>
                    <Grid container>
                        <Typography variant="h3">{userAgreement?.name}</Typography>
                    </Grid>
                    <TooltipButton
                        circled
                        title={t(`common::label::Close`)}
                        onClickButton={onClose}
                        buttonIcon={<IconClose size={20} htmlColor={theme.palette.secondary[700]} />}
                    />
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <iframe
                        src={disagreedPart?.document?.accessUrl}
                        title={t(`common::label::Terms and Conditions`, `Terms and Conditions`)}
                        width="100%"
                        height="500px"
                        style={{ border: "none", display: "block", margin: 0, padding: 0 }}
                    />
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Grid container flexDirection={"row"} justifyContent="space-between" alignItems="center">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color={"greySecondary"}
                                    checked={hasAgreed}
                                    onClick={(e) => {
                                        setHasAgreed(e.target.checked);
                                    }}
                                />
                            }
                            label={
                                <Typography variant={"body1_500"} sx={{ ml: 0.75 }}>
                                    {t(
                                        `common::msg::I agree to the above terms and conditions`,
                                        `I agree to the above
                                    terms and conditions`,
                                    )}
                                </Typography>
                            }
                        />
                        <Stack direction={"row"} spacing={1} alignItems="center">
                            <Button color="secondary" onClick={onClose} variant="text">
                                {t(`common::label::Cancel`, `Cancel`)}
                            </Button>
                            <Button
                                color="primary"
                                onClick={onClickCheckAgreement}
                                variant="contained"
                                disabled={!hasAgreed || updatePending}
                                loading={updatePending}
                            >
                                {t(`common::label::Check`, `Check`)}
                            </Button>
                        </Stack>
                    </Grid>
                </DialogActions>
            </StickyHeaderDialog>
            {children}
        </>
    );
};

export default UserAgreement;
